import { CurrentValue, Session, Settings } from "@/api/types";
import { DXNews, DXUserRights } from "@/types";
import { defineStore } from "pinia";

export const useUserConfigStore = defineStore("userConfig", {
  state: () => ({
    maxDashboards: 99,
    maxCards: 99,
    maxWidgets: 99,
    featureLimit: false,
    userName: false, //! not present in store anymore
    userContract: false, //! not present in store anymore
    newsList: [] as DXNews[],
    userData: {} as Partial<Session>,
    current: [] as CurrentValue[],
    completedTours: [] as string[],
    userSettings: {} as Settings,
    userRights: {} as DXUserRights,
  }),
  getters: {
    isRCSetupComplete(state) {
      return [state.userData.remoteControlSLA, state.userData.remoteControlTOTP];
    },
    hasMathFunctionality(state) {
      return state.userData?.accessRights?.["access-math-functionality"] === true;
    },
    hasAdvancedReporting(state) {
      return state.userData?.accessRights?.["access-advanced-reporting"] === true;
    },
    hasAccessToMultiTenancy(state) {
      return state.userData?.accessRights?.["access-multi-tenancy"] === true;
    },
    hasWLTools(state) {
      return state.userRights.create_whitelabels;
    },
    checkPermission: (state) => (permissionKey: keyof DXUserRights | undefined) => {
      // In case the required permission key is not found, which means the page does not require permission.
      if (!permissionKey) {
        return true;
      }
      try {
        if (state.userRights[permissionKey] === true) {
          return true;
        } else {
          return false;
        }
      } catch (error) {
        // In case of error, do not give permission.
        return false;
      }
    },
    canAddDevice(state) {
      if (!state.userRights.login_platform) {
        return false;
      }
      return state.userRights.add_device || state.userRights.add_new_device || state.userRights.add_any_device_directly;
    },
    canManageProfile(state) {
      if (!state.userRights.login_platform) {
        return false;
      }
      return state.userRights.manage_profile;
    },
    canManageAlarms(state) {
      if (!state.userRights.login_platform) {
        return false;
      }
      return state.userRights.manage_alarms;
    },
    canManageUsers(state) {
      if (!state.userRights.login_platform) {
        return false;
      }
      return state.userRights.manage_contract_users;
    },
    canManageRemoteControl(state) {
      return state.userRights.remote_control_management;
    },
    canActivateValueFilter(state) {
      return state.userRights.device_values_filter_activation;
    },
    canChangeDeviceSettings(state) {
      return state.userRights.configure_devices;
    },
    canRemotelyControlDevices(state) {
      return (
        state.userRights.remote_control_basic ||
        state.userRights.remote_control_extended ||
        state.userRights.remote_control_full ||
        state.userRights.remote_control_management
      );
    },
    canAddPredefinedCalculations(state) {
      return state.userRights.math_add_predefined_calculation;
    },
    canManageVirtualDevices(state) {
      return state.userRights.manage_virtual_devices;
    },
    canEditDeviceLocation(state) {
      return state.userRights?.set_device_location ?? false;
    },
    canViewVirtualDeviceTour(state) {
      return state.userData?.allowedTours?.["view_tour_virtual_device"] ?? true;
    },
    canViewVirtualVariableTour(state) {
      return state.userData?.allowedTours?.["view_tour_virtual_variable"] ?? true;
    },
    canViewMathEditorTour(state) {
      return state.userData?.allowedTours?.["view_tour_math_editor"] ?? true;
    },
    canViewDeviceReportsTour(state) {
      return state.userData?.allowedTours?.["view_tour_device_reports"] ?? true;
    },
  },
});